import { AfterContentInit, AfterViewInit, Component, ContentChild, ElementRef, Input, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { NgModel } from '@angular/forms';

import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';

import { escape, isString } from 'rev-shared/util';
import { VbUiDataGridComponent } from 'rev-shared/ui/dataGrid/VbUiDataGrid.Component';
import { VbUiInfiniteScrollGridComponent } from 'rev-shared/ui/dataGrid/infiniteScroll/VbUiInfiniteScrollGrid.Component';

import { GridType } from './Constant';

import styles from './ReportGridWrapper.Component.module.less';

const MIN_SEARCH_LENGTH: number = 2;
const SEARCH_DEBOUNCE_MS: number = 500;

@Component({
	selector: 'report-grid-wrapper',
	templateUrl: './ReportGridWrapper.Component.html',
	host: {
		class: 'height-full width-full',
		layout: 'column',
		'layout-wrap': 'false'
	}
})
export class ReportGridWrapperComponent implements AfterContentInit, AfterViewInit, OnDestroy {
	@Input() public accountId: string;
	@Input() public disableColumnPicker: number;
	@Input() public header: string;
	@Input() public headerTag: string;
	@Input() public headerTemplate: TemplateRef<any>;
	@Input() public isInfiniteScroll: boolean;
	@Input() public pollingIntervalSecs: number;
	@Input() public type: GridType;

	@ContentChild(VbUiDataGridComponent) public normalGrid: VbUiDataGridComponent;
	@ContentChild(VbUiDataGridComponent, { read: ElementRef }) private gridElement: ElementRef<HTMLElement>;

	@ContentChild(VbUiInfiniteScrollGridComponent) public infiniteGrid: VbUiInfiniteScrollGridComponent;
	@ContentChild(VbUiInfiniteScrollGridComponent, { read: ElementRef }) private infiniteGridElement: ElementRef<HTMLElement>;

	@ViewChild('searchInputNgModel') private searchInput: NgModel;

	public searchInputValue: string;
	public readonly styles = styles;
	public readonly escape = escape;
	public grid: VbUiDataGridComponent | VbUiInfiniteScrollGridComponent;

	private subscriptions: Subscription[];

	public ngAfterContentInit(): void {
		this.grid = this.normalGrid || this.infiniteGrid;
		this.gridElement = this.gridElement || this.infiniteGridElement;

		this.gridElement.nativeElement.setAttribute('flex', 'fill');
		this.gridElement.nativeElement.classList.add('height-full');

		this.grid.suppressMultiSort = true;
		this.grid.columnPickerEnabled = !this.disableColumnPicker;
		this.grid.accentedSort = true;
		this.grid.context = {
			...this.grid.context,
			accountId: this.accountId,
			type: this.type
		};
		this.grid.defaultColDef = {
			...this.grid.defaultColDef,
			minWidth: 270,
			resizable: true
		};

		if (this.grid instanceof VbUiInfiniteScrollGridComponent && this.pollingIntervalSecs) {
			this.grid.pollingIntervalSecs = this.pollingIntervalSecs;
		}
	}

	public ngAfterViewInit(): void {
		this.subscriptions = [
			this.initSearchInputSubscription()
		];
	}

	public ngOnDestroy(): void {
		this.subscriptions?.forEach(sub => sub.unsubscribe());
	}

	private initSearchInputSubscription(): Subscription {
		return this.searchInput.valueChanges
			.pipe(
				filter(text => isString(text)),
				map(text => text.trim()),
				filter(text => !text.length || text.length >= MIN_SEARCH_LENGTH),
				debounceTime(SEARCH_DEBOUNCE_MS),
				distinctUntilChanged()
			)
			.subscribe(value => this.grid.setQuickFilterText(value));
	}
}
